import { supabase } from './supabase';
import { triggerBookingAutomations } from './automationsV2';

export interface Payment {
  id: string;
  business_id: string;
  booking_id: string;
  amount: number;
  method: 'credit_card' | 'debit_card' | 'cash' | 'bank_transfer' | 'check' | 'other';
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'voided';
  reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  booking_number?: number;
  booking_title?: string;
  booking_total?: number;
  booking_subtotal?: number;
  booking_tax_amount?: number;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  booking_services?: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    tax_amount: number;
    total_amount: number;
    duration_minutes: number;
    vehicle_make: string;
    vehicle_model: string;
  }[];
  booking_discounts?: {
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }[];
}

export async function getPayments(businessId: string) {
  const { data, error } = await supabase
    .from('payments_with_details')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Payment[];
}

async function getBookingPayments(bookingId: string) {
  const { data, error } = await supabase
    .from('payments_with_details')
    .select('*')
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Payment[];
}

export async function createPayment(payment: Omit<Payment, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('payments')
    .insert([payment])
    .select()
    .single();

  if (error) throw error;

  // Trigger automation if payment is completed
  if (payment.status === 'completed') {
    try {
      // Get booking details to calculate payment status
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          *,
          customer:customers(*),
          services:booking_services(*),
          payments:payments!inner(amount, status)
        `)
        .eq('id', payment.booking_id)
        .single();

      if (!bookingError && bookingData) {
        // Calculate total paid amount (including this new payment)
        const totalPaid = bookingData.payments
          .filter((p: any) => p.status === 'completed')
          .reduce((sum: number, p: any) => sum + p.amount, 0);

        const bookingTotal = bookingData.total_amount || 0;
        const remainingBalance = Math.round((bookingTotal - totalPaid) * 100) / 100;

        // Determine payment event type
        let eventType: 'partially_paid' | 'paid' | null = null;
        
        if (remainingBalance <= 0) {
          // Fully paid
          eventType = 'paid';
        } else if (totalPaid > 0) {
          // Partially paid (there's still a balance)
          eventType = 'partially_paid';
        }

        // Trigger automation if we have a payment event
        if (eventType) {
          console.log(`💰 Payment recorded: ${eventType} for booking ${bookingData.id}`);
          
          await triggerBookingAutomations(
            payment.business_id,
            eventType,
            {
              ...bookingData,
              // Include payment information in context
              payment_amount: payment.amount,
              total_paid: totalPaid,
              remaining_balance: remainingBalance,
              payment_method: payment.method,
              payment_reference: payment.reference
            }
          );
        }
      }
    } catch (automationError) {
      // Log but don't throw - payment was successful, automation failure shouldn't break the flow
      console.error('Failed to trigger payment automation:', automationError);
    }
  }

  return data as Payment;
}

async function updatePayment(id: string, updates: Partial<Payment>) {
  const { data, error } = await supabase
    .from('payments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}

async function deletePayment(id: string) {
  const { error } = await supabase
    .from('payments')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function voidPayment(id: string, reason: string) {
  const { data, error } = await supabase
    .from('payments')
    .update({
      status: 'voided',
      notes: reason
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}

async function refundPayment(id: string, amount: number, reason: string) {
  const { data, error } = await supabase
    .from('payments')
    .update({
      status: 'refunded',
      notes: reason
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Payment;
}